/**
 * Library set hooks — kind-parameterized queries + the write mutations.
 * Every write invalidates `libraryKeys.all` (usage counts cross-cut). No
 * optimistic updates: counts feed the fork dialog and must be server-truth.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { libraryKeys } from '../keys';
import {
  createSet,
  deleteSet,
  forkSet,
  listSets,
  updateSet,
} from '../client/sets-api';
import type {
  ForkSetBody,
  LibrarySetKind,
  LibrarySetWriteBody,
} from '../client/sets-api';

export function useLibrarySets(kind: LibrarySetKind, enabled = true) {
  return useQuery({
    queryKey: libraryKeys.sets(kind),
    queryFn: () => listSets(kind),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetMutations(kind: LibrarySetKind) {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: libraryKeys.all });
  const create = useMutation({
    mutationFn: (body: LibrarySetWriteBody & { name: string }) =>
      createSet(kind, body),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: (input: { id: string; body: LibrarySetWriteBody }) =>
      updateSet(kind, input.id, input.body),
    onSuccess: invalidate,
  });
  const fork = useMutation({
    mutationFn: (input: { id: string; body: ForkSetBody }) =>
      forkSet(kind, input.id, input.body),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteSet(kind, id),
    onSuccess: invalidate,
  });
  return { create, update, fork, remove };
}
